function parseInitialView(fieldRoot) {
  try {
    return JSON.parse(fieldRoot.dataset.initialView || '{}');
  } catch (error) {
    return {};
  }
}

function resolveEmbedContext(fieldRoot) {
  if (!fieldRoot || fieldRoot.dataset.embedMode !== '1') {
    return null;
  }

  let token = fieldRoot.dataset.embedToken || '';
  let workHashId = fieldRoot.dataset.workHashId || '';
  let lang = fieldRoot.dataset.embedLang || 'ru';

  if (!token || !workHashId) {
    const pathMatch = window.location.pathname.match(
      /\/(?:frame|wb)\/([a-zA-Z0-9]+)-([a-zA-Z0-9]+)\//,
    );

    if (pathMatch) {
      token = token || pathMatch[1];
      workHashId = workHashId || pathMatch[2];
    }
  }

  if (token && !fieldRoot.dataset.embedToken) {
    fieldRoot.dataset.embedToken = token;
  }

  if (workHashId && !fieldRoot.dataset.workHashId) {
    fieldRoot.dataset.workHashId = workHashId;
  }

  return { token, workHashId, lang };
}

function appendEmbedCredentials(formData, fieldRoot) {
  const embedContext = resolveEmbedContext(fieldRoot);

  if (!embedContext) {
    return;
  }

  const { token, workHashId, lang } = embedContext;

  if (token) {
    formData.append('token', token);
  }

  if (workHashId) {
    formData.append('workHashId', workHashId);
  }

  if (lang) {
    formData.append('lang', lang);
  }
}

function appendEmbedQueryParams(url, fieldRoot) {
  const embedContext = resolveEmbedContext(fieldRoot);

  if (!embedContext) {
    return url;
  }

  const { token, workHashId, lang } = embedContext;
  const nextUrl = new URL(url, window.location.origin);

  if (token) {
    nextUrl.searchParams.set('token', token);
  }

  if (workHashId) {
    nextUrl.searchParams.set('workHashId', workHashId);
  }

  if (lang) {
    nextUrl.searchParams.set('lang', lang);
  }

  return nextUrl.toString();
}

async function postAiImageForm(url, formData) {
  const response = await fetch(url, {
    method: 'POST',
    body: formData,
    credentials: 'same-origin',
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
    },
  });

  let payload;

  try {
    payload = await response.json();
  } catch (error) {
    if (response.status === 404) {
      throw new Error('Заявка не найдена');
    }

    throw new Error('Сервер вернул некорректный ответ');
  }

  if (payload.status !== 'ok') {
    throw new Error((payload.message && payload.message[0]) || 'Ошибка запроса');
  }

  return payload;
}

const AI_IMAGE_ACTION_SELECTOR = [
  '.js-ai-image-reopen-pick',
  '.js-ai-image-generate',
  '.js-ai-image-generate-more',
  '.js-ai-image-keep',
].join(', ');

const PROCESSING_MESSAGES = [
  'Загружаем фотографию',
  'Творим магию',
  'Выставляем ракурс',
];

const PROCESSING_MESSAGE_INTERVAL_MS = 6000;

const PENDING_JOB_STATUSES = ['wait', 'processing'];

function mergeAiImageView(previous, incoming) {
  const prev = previous || {};
  const next = incoming || {};
  const merged = { ...prev, ...next };

  if (!merged.sourceImage?.url && prev.sourceImage?.url) {
    merged.sourceImage = prev.sourceImage;
  } else if (merged.sourceImage?.url && prev.sourceImage?.url && !merged.sourceImage.id && prev.sourceImage.id) {
    merged.sourceImage = { ...prev.sourceImage, ...merged.sourceImage };
  }

  if (
    (!Array.isArray(merged.candidates) || merged.candidates.length === 0)
    && Array.isArray(prev.candidates)
    && prev.candidates.length > 0
  ) {
    merged.candidates = prev.candidates;
  }

  if (!merged.selectedImage?.url && prev.selectedImage?.url) {
    merged.selectedImage = prev.selectedImage;
  }

  if (
    (!Array.isArray(merged.scenes) || merged.scenes.length === 0)
    && Array.isArray(prev.scenes)
    && prev.scenes.length > 0
  ) {
    merged.scenes = prev.scenes;
  }

  if (!merged.selectedSceneId && prev.selectedSceneId) {
    merged.selectedSceneId = prev.selectedSceneId;
  }

  if (!merged.selectedImageId && prev.selectedImageId) {
    merged.selectedImageId = prev.selectedImageId;
  }

  return merged;
}

function resolveScreenFromView(view) {
  const candidates = view.candidates || [];
  const generationsUsed = Number(view.generationsUsed ?? 0);
  const selectionConfirmed = Boolean(view.selectionConfirmed);
  const selectedImageId = Number(view.selectedImageId ?? 0);

  if (selectionConfirmed && selectedImageId > 0) {
    return 'saved';
  }

  if (candidates.length >= 2) {
    return 'pick';
  }

  if (candidates.length >= 1 || view.selectedImage?.url) {
    return 'result';
  }

  if (view.sourceImage?.url) {
    return generationsUsed > 0 ? 'result' : 'source-ready';
  }

  return 'upload';
}

function normalizeViewFromJob(job, baseView, jobUuid = '') {
  const view = mergeAiImageView(baseView, job.view);
  const jobStatus = job.status;

  if (PENDING_JOB_STATUSES.includes(jobStatus)) {
    return {
      ...view,
      screen: 'processing',
      isProcessing: true,
      pollJobUuid: jobUuid || view.pollJobUuid || '',
    };
  }

  if (jobStatus === 'done') {
    const nextView = {
      ...view,
      isProcessing: false,
      pollJobUuid: '',
    };

    if (job.image?.id) {
      const candidates = [...(nextView.candidates || [])];
      const hasCandidate = candidates.some((candidate) => candidate.id === job.image.id);

      if (!hasCandidate) {
        candidates.push({
          id: job.image.id,
          url: job.image.url,
          label: `Вариант ${candidates.length + 1}`,
        });
      }

      nextView.candidates = candidates;

      if (!nextView.selectedImage?.url) {
        nextView.selectedImage = job.image;
        nextView.selectedImageId = job.image.id;
      }
    }

    nextView.screen = resolveScreenFromView(nextView);

    return nextView;
  }

  if (jobStatus === 'error') {
    const errorView = {
      ...view,
      isProcessing: false,
      pollJobUuid: '',
      canGenerate: view.canGenerate !== false,
    };

    errorView.screen = resolveScreenFromView(errorView);

    return errorView;
  }

  return view;
}

function pluralizeGenerations(count) {
  const value = Math.abs(Number(count) || 0);
  const mod10 = value % 10;
  const mod100 = value % 100;

  if (mod10 === 1 && mod100 !== 11) {
    return 'генерация';
  }

  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
    return 'генерации';
  }

  return 'генераций';
}

const fieldControllers = new WeakMap();
let documentClickBound = false;

function bindDocumentClick() {
  if (documentClickBound) {
    return;
  }

  documentClickBound = true;

  document.addEventListener('click', (event) => {
    const actionButton = event.target.closest(AI_IMAGE_ACTION_SELECTOR);

    if (!actionButton) {
      return;
    }

    const fieldRoot = actionButton.closest('.js-ai-image-field');

    if (!fieldRoot) {
      return;
    }

    let controller = fieldControllers.get(fieldRoot);

    if (!controller) {
      try {
        controller = createAiImageFieldController(fieldRoot);
        fieldControllers.set(fieldRoot, controller);
        fieldRoot.dataset.aiImageInitialized = '1';
      } catch (error) {
        console.error('[ai-image-field] init failed', error);
        return;
      }
    }

    controller.handleActionClick(event, actionButton);
  }, true);
}

function initAiImageFields() {
  bindDocumentClick();

  document.querySelectorAll('.js-ai-image-field').forEach((fieldRoot) => {
    if (fieldRoot.dataset.aiImageInitialized === '1') {
      return;
    }

    try {
      const controller = createAiImageFieldController(fieldRoot);
      fieldControllers.set(fieldRoot, controller);
      fieldRoot.dataset.aiImageInitialized = '1';
    } catch (error) {
      console.error('[ai-image-field] init failed', error);
    }
  });
}

function createAiImageFieldController(fieldRoot) {
  const fileInput = fieldRoot.querySelector('.js-ai-image-upload-input');
  const errorBlock = fieldRoot.querySelector('.js-ai-image-upload-error');
  const disabledNotice = fieldRoot.querySelector('.js-ai-image-disabled-notice');
  const fileIdInput = fieldRoot.querySelector('.js-ai-image-file-id');
  const screens = fieldRoot.querySelectorAll('.js-ai-image-screen');
  const dropzone = fieldRoot.querySelector('.js-ai-image-dropzone');
  const fileInputId = fileInput?.id || '';
  const sourcePreview = fieldRoot.querySelector('.js-ai-image-source-preview');
  const resultPreview = fieldRoot.querySelector('.js-ai-image-result-preview');
  const savedPreview = fieldRoot.querySelector('.js-ai-image-saved-preview');
  const candidatesRoot = fieldRoot.querySelector('.js-ai-image-candidates');
  const processingTitle = fieldRoot.querySelector('.js-ai-image-processing-title');
  const processingStatus = fieldRoot.querySelector('.js-ai-image-processing-status');
  const remainingCountNodes = fieldRoot.querySelectorAll('.js-ai-image-remaining-count');
  const availableCountNodes = fieldRoot.querySelectorAll('.js-ai-image-available-count');
  const availableLabelNodes = fieldRoot.querySelectorAll('.js-ai-image-available-label');
  const maxGenerationsNodes = fieldRoot.querySelectorAll('.js-ai-image-max-generations');
  const sourceBars = fieldRoot.querySelectorAll('.js-ai-image-source-bar');
  const sceneSections = fieldRoot.querySelectorAll('.js-ai-image-scenes');
  const currentSceneTitleNodes = fieldRoot.querySelectorAll('.js-ai-image-current-scene-title');
  const currentSceneBadge = fieldRoot.querySelector('.js-ai-image-current-scene-badge');

  const uploadUrl = fieldRoot.dataset.uploadUrl;
  const generateUrl = fieldRoot.dataset.generateUrl;
  const selectUrl = fieldRoot.dataset.selectUrl;
  const reopenPickUrl = fieldRoot.dataset.reopenPickUrl;
  const statusUrlTemplate = fieldRoot.dataset.statusUrlTemplate;

  let workFieldUuid = fieldRoot.dataset.workFieldUuid;
  const customFieldUuid = fieldRoot.dataset.customFieldUuid;

  const syncWorkFieldUuidInDom = (nextWorkFieldUuid) => {
    const normalizedUuid = String(nextWorkFieldUuid || '').trim();

    if (normalizedUuid === '') {
      return;
    }

    const previousUuid = String(workFieldUuid || fieldRoot.dataset.workFieldUuid || '').trim();

    workFieldUuid = normalizedUuid;
    fieldRoot.dataset.workFieldUuid = normalizedUuid;
    fieldRoot.dataset.uuid = normalizedUuid;

    if (previousUuid !== '' && previousUuid !== normalizedUuid) {
      const previousFieldPrefix = `custom_fields[${previousUuid}]`;

      fieldRoot.querySelectorAll('input[name^="custom_fields["]').forEach((input) => {
        if (!input.name.startsWith(previousFieldPrefix)) {
          return;
        }

        input.name = input.name.replace(previousFieldPrefix, `custom_fields[${normalizedUuid}]`);
      });
    }

    const workFieldUuidInput = fieldRoot.querySelector('input[name*="[custom_work_field_uuid]"]');

    if (workFieldUuidInput) {
      workFieldUuidInput.value = normalizedUuid;
    }
  };
  let viewState = parseInitialView(fieldRoot);

  if (viewState.isProcessing && !viewState.pollJobUuid) {
    viewState = {
      ...viewState,
      isProcessing: false,
      screen: resolveScreenFromView({ ...viewState, isProcessing: false }),
    };
  }
  let selectedCandidateId = viewState.selectedImageId || 0;
  let selectedSceneId = viewState.selectedSceneId || '';
  /** Явный выбор пользователя — не перезаписывается ответами сервера при polling/upload. */
  let userPickedSceneId = fieldRoot.querySelector('.js-ai-image-scene-id')?.value?.trim() || '';
  let pollTimer = null;
  let processingMessageTimer = null;
  let processingMessageIndex = 0;
  let actionPending = false;
  let isReplaceUpload = false;
  let sourceUploadConfirmed = Boolean(viewState.sourceImage?.url);
  let lastKnownSourceImage = viewState.sourceImage?.url ? viewState.sourceImage : null;

  const showSourceReadyScreen = () => {
    applyViewState({
      ...viewState,
      screen: 'source-ready',
      isProcessing: false,
      pollJobUuid: '',
    });
  };

  const setActionPending = (isPending) => {
    actionPending = isPending;
    fieldRoot.classList.toggle('is-action-pending', isPending);
  };

  const setButtonsBusy = (isBusy, busyLabel = '') => {
    fieldRoot.querySelectorAll('.js-ai-image-generate, .js-ai-image-generate-more, .js-ai-image-keep').forEach((button) => {
      if (isBusy) {
        if (!button.dataset.originalLabel) {
          button.dataset.originalLabel = button.textContent.trim();
        }

        button.disabled = true;
        button.setAttribute('aria-busy', 'true');

        if (busyLabel && (button.classList.contains('js-ai-image-generate') || button.classList.contains('js-ai-image-generate-more'))) {
          button.textContent = busyLabel;
        }
      } else if (button.dataset.originalLabel) {
        button.textContent = button.dataset.originalLabel;
        delete button.dataset.originalLabel;
        button.removeAttribute('aria-busy');
      }
    });
  };

  const stopPolling = () => {
    if (pollTimer) {
      window.clearTimeout(pollTimer);
      pollTimer = null;
    }
  };

  const setProcessingStatusText = (message) => {
    if (processingStatus) {
      processingStatus.textContent = message;
    }
  };

  const stopProcessingMessageRotation = () => {
    if (processingMessageTimer) {
      window.clearInterval(processingMessageTimer);
      processingMessageTimer = null;
    }
  };

  const startProcessingMessageRotation = () => {
    stopProcessingMessageRotation();
    processingMessageIndex = 0;
    setProcessingStatusText(PROCESSING_MESSAGES[0]);

    processingMessageTimer = window.setInterval(() => {
      processingMessageIndex = (processingMessageIndex + 1) % PROCESSING_MESSAGES.length;
      setProcessingStatusText(PROCESSING_MESSAGES[processingMessageIndex]);
    }, PROCESSING_MESSAGE_INTERVAL_MS);
  };

  const showError = (message) => {
    if (!errorBlock) {
      return;
    }

    errorBlock.textContent = message;
    errorBlock.classList.add('is-visible');
  };

  const clearError = () => {
    if (!errorBlock) {
      return;
    }

    errorBlock.textContent = '';
    errorBlock.classList.remove('is-visible');
  };

  const setScreen = (screenName) => {
    screens.forEach((screen) => {
      const isActive = screen.dataset.screen === screenName;
      screen.hidden = !isActive;
    });
  };

  const isSceneIdValid = (sceneId) => {
    if (!sceneId) {
      return false;
    }

    return (viewState.scenes || []).some((scene) => scene.id === sceneId);
  };

  const sceneIdInput = fieldRoot.querySelector('.js-ai-image-scene-id');

  const syncSceneIdInput = (sceneId) => {
    if (sceneIdInput) {
      sceneIdInput.value = sceneId;
    }
  };

  const pickScene = (sceneId) => {
    if (!isSceneIdValid(sceneId)) {
      return '';
    }

    userPickedSceneId = sceneId;
    selectedSceneId = sceneId;
    fieldRoot.dataset.userSceneId = sceneId;
    syncSceneIdInput(sceneId);

    return sceneId;
  };

  const ensureSelectedScene = () => {
    const scenes = viewState.scenes || [];

    if (userPickedSceneId && scenes.some((scene) => scene.id === userPickedSceneId)) {
      selectedSceneId = userPickedSceneId;
      return selectedSceneId;
    }

    if (selectedSceneId && scenes.some((scene) => scene.id === selectedSceneId)) {
      return selectedSceneId;
    }

    if (viewState.selectedSceneId && scenes.some((scene) => scene.id === viewState.selectedSceneId)) {
      selectedSceneId = viewState.selectedSceneId;
      syncSceneIdInput(selectedSceneId);

      return selectedSceneId;
    }

    return '';
  };

  const resolveGenerationSceneId = () => {
    const hiddenSceneId = sceneIdInput?.value?.trim() || '';

    if (userPickedSceneId && isSceneIdValid(userPickedSceneId)) {
      return userPickedSceneId;
    }

    if (hiddenSceneId && isSceneIdValid(hiddenSceneId)) {
      return pickScene(hiddenSceneId);
    }

    const visibleSceneSection = fieldRoot.querySelector('.js-ai-image-scenes:not([hidden])');
    const domSelected = visibleSceneSection
      ?.querySelector('.js-ai-image-scene.is-selected')
      ?.dataset.sceneId || '';
    const datasetSelected = fieldRoot.dataset.userSceneId || '';

    if (domSelected && isSceneIdValid(domSelected)) {
      return pickScene(domSelected);
    }

    if (datasetSelected && isSceneIdValid(datasetSelected)) {
      return pickScene(datasetSelected);
    }

    return ensureSelectedScene();
  };

  const renderScenes = () => {
    const scenes = viewState.scenes || [];

    sceneSections.forEach((section) => {
      const grid = section.querySelector('.js-ai-image-scenes-grid');
      const titleNode = section.querySelector('.js-ai-image-scenes-title');
      const mode = section.dataset.scenesMode || 'initial';
      const isVisible = viewState.screen === 'source-ready'
        || (mode === 'next' && (viewState.screen === 'result' || viewState.screen === 'pick'));

      section.hidden = !isVisible;

      if (!isVisible || !grid) {
        return;
      }

      if (titleNode) {
        titleNode.textContent = mode === 'initial'
          ? 'Выберите сцену'
          : 'Сцена для следующей генерации';
      }

      grid.innerHTML = '';

      scenes.forEach((scene) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ai-image-field__scene js-ai-image-scene';
        button.dataset.sceneId = scene.id;

        if (selectedSceneId === scene.id) {
          button.classList.add('is-selected');
        }

        button.innerHTML = `
          <span class="ai-image-field__scene-check" aria-hidden="true"></span>
          <img class="ai-image-field__scene-preview" src="${scene.previewUrl}" alt="">
          <span class="ai-image-field__scene-title">${scene.title}</span>
        `;

        button.addEventListener('click', () => {
          if (actionPending) {
            return;
          }

          pickScene(scene.id);
          renderScenes();
          updateGenerateButtonsState();
        });

        grid.appendChild(button);
      });
    });
  };

  const updateInteractionState = () => {
    const disabled = viewState.interactionDisabled === true;

    fieldRoot.classList.toggle('is-interaction-disabled', disabled);

    if (disabledNotice) {
      if (disabled) {
        disabledNotice.hidden = false;
        disabledNotice.textContent = viewState.interactionDisabledReason
          || 'Заявка удалена. Генерация изображений недоступна.';
      } else {
        disabledNotice.hidden = true;
        disabledNotice.textContent = '';
      }
    }

    if (fileInput) {
      fileInput.disabled = disabled;
    }

    if (!disabled) {
      return;
    }

    stopPolling();
    stopProcessingMessageRotation();
    setActionPending(false);
    setButtonsBusy(false);

    fieldRoot.querySelectorAll(AI_IMAGE_ACTION_SELECTOR).forEach((button) => {
      button.disabled = true;
    });

    fieldRoot.querySelectorAll('.js-ai-image-scene').forEach((button) => {
      button.disabled = true;
    });

    fieldRoot.querySelectorAll('.js-ai-image-keep').forEach((button) => {
      button.disabled = true;
    });
  };

  const updateGenerateButtonsState = () => {
    ensureSelectedScene();

    const isLimitReached = (viewState.generationsRemaining ?? 0) <= 0;
    const isBusy = Boolean(viewState.isProcessing) && viewState.screen === 'processing';
    const hasScene = Boolean(selectedSceneId);
    const canGenerate = viewState.canGenerate !== false;
    const hasConfirmedSource = sourceUploadConfirmed && Boolean(viewState.sourceImage?.url);

    fieldRoot.querySelectorAll('.js-ai-image-generate, .js-ai-image-generate-more').forEach((button) => {
      button.disabled = isLimitReached || isBusy || actionPending || !hasScene || !canGenerate || !hasConfirmedSource;
      button.hidden = isLimitReached && !isBusy;
    });
  };

  const renderSourceBars = () => {
    const hasGeneratedVariants = (viewState.candidates || []).length > 0
      || Boolean(viewState.selectedImage)
      || viewState.screen === 'result'
      || viewState.screen === 'pick';

    if (viewState.screen === 'saved' || viewState.screen === 'source-ready' || viewState.screen === 'upload') {
      sourceBars.forEach((bar) => {
        bar.innerHTML = '';
        bar.hidden = true;
      });

      return;
    }

    if (!viewState.sourceImage && !hasGeneratedVariants) {
      sourceBars.forEach((bar) => {
        bar.innerHTML = '';
        bar.hidden = true;
      });

      return;
    }

    const thumbHtml = viewState.sourceImage?.url
      ? `<img class="ai-image-field__source-thumb" src="${viewState.sourceImage.url}" alt="">`
      : '<div class="ai-image-field__source-thumb ai-image-field__source-thumb--empty" aria-hidden="true"></div>';

    const sourceTitle = viewState.sourceImage
      ? `Исходное фото · ${viewState.sourceImage.fileName || 'photo.jpg'}`
      : 'Исходное фото · загрузите заново, если нужно заменить';

    const html = `
      <div class="ai-image-field__source-info">
        ${thumbHtml}
        <div class="ai-image-field__source-text">
          <div class="ai-image-field__source-title">${sourceTitle}</div>
          <div class="ai-image-field__source-caption">Счётчик генераций не сбрасывается</div>
        </div>
      </div>
      <label
        for="${fileInputId}"
        class="js-ai-image-file-trigger ai-image-field__btn ai-image-field__btn--outline ai-image-field__btn--source-replace"
        data-replace="1"
      >
        <span class="ai-image-field__btn-icon" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 7H4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
            <path d="M6 3L3 6L6 9" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M4 17H20" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
            <path d="M18 21L21 18L18 15" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
        Заменить фото
      </label>
    `;

    sourceBars.forEach((bar) => {
      bar.hidden = false;
      bar.innerHTML = html;
    });
  };

  const renderCandidates = () => {
    if (!candidatesRoot) {
      return;
    }

    candidatesRoot.innerHTML = '';

    (viewState.candidates || []).forEach((candidate, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ai-image-field__candidate js-ai-image-candidate';
      button.dataset.imageId = String(candidate.id);

      if (selectedCandidateId === candidate.id || (!selectedCandidateId && index === viewState.candidates.length - 1)) {
        button.classList.add('is-selected');
        selectedCandidateId = candidate.id;
      }

      button.innerHTML = `
        <span class="ai-image-field__candidate-check" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 12.5L10 16.5L18 8.5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
        <img class="ai-image-field__candidate-image" src="${candidate.url}" alt="">
        <div class="ai-image-field__candidate-footer">
          <span class="ai-image-field__candidate-label">${candidate.label || `Вариант ${index + 1}`}</span>
          ${candidate.sceneTitle ? `<span class="ai-image-field__candidate-scene">${candidate.sceneTitle}</span>` : ''}
        </div>
      `;

      button.addEventListener('click', () => {
        if (actionPending) {
          return;
        }

        selectedCandidateId = candidate.id;
        selectCandidate(candidate.id).catch((error) => {
          showError(error.message || 'Ошибка сохранения');
        });
      });

      candidatesRoot.appendChild(button);
    });
  };

  const pinSourceImage = (sourceImage) => {
    if (!sourceImage?.url) {
      return;
    }

    lastKnownSourceImage = sourceImage;
    sourceUploadConfirmed = true;
    fieldRoot.dataset.sourceImageUrl = sourceImage.url;
    fieldRoot.dataset.sourceImageId = sourceImage.id ? String(sourceImage.id) : '';
    fieldRoot.dataset.sourceImageName = sourceImage.fileName || '';
  };

  const restorePinnedSourceImage = () => {
    if (lastKnownSourceImage?.url) {
      return lastKnownSourceImage;
    }

    const url = fieldRoot.dataset.sourceImageUrl || '';

    if (!url) {
      return null;
    }

    lastKnownSourceImage = {
      id: Number(fieldRoot.dataset.sourceImageId || 0) || 0,
      url,
      fileName: fieldRoot.dataset.sourceImageName || '',
    };

    return lastKnownSourceImage;
  };

  const applyViewState = (nextView) => {
    viewState = mergeAiImageView(viewState, nextView || {});

    if (viewState.sourceImage?.url) {
      pinSourceImage(viewState.sourceImage);
    } else {
      const pinnedSource = restorePinnedSourceImage();

      if (pinnedSource) {
        viewState.sourceImage = pinnedSource;
        sourceUploadConfirmed = true;
      }
    }

    if (viewState.sourceImage?.url && viewState.screen === 'upload' && !viewState.isProcessing) {
      viewState.screen = resolveScreenFromView({
        ...viewState,
        screen: 'source-ready',
      });
    }

    fieldRoot.dataset.processingStatus = viewState.processingStatus || '';
    fieldRoot.dataset.activeJobUuid = viewState.pollJobUuid || '';

    const scenes = viewState.scenes || [];
    const userPickStillValid = userPickedSceneId
      && scenes.some((scene) => scene.id === userPickedSceneId);

    if (userPickStillValid) {
      selectedSceneId = userPickedSceneId;
      syncSceneIdInput(userPickedSceneId);
    } else if (!actionPending && viewState.selectedSceneId) {
      const localSelectionStillValid = selectedSceneId
        && scenes.some((scene) => scene.id === selectedSceneId);

      if (!localSelectionStillValid) {
        selectedSceneId = viewState.selectedSceneId;
        syncSceneIdInput(selectedSceneId);
      }
    }

    if (!userPickStillValid && !userPickedSceneId && viewState.selectedSceneId) {
      const syncedSceneId = ensureSelectedScene();
      if (syncedSceneId) {
        syncSceneIdInput(syncedSceneId);
      }
    }

    remainingCountNodes.forEach((node) => {
      node.textContent = String(viewState.generationsRemaining ?? 0);
    });

    availableCountNodes.forEach((node) => {
      node.textContent = String(viewState.generationsRemaining ?? viewState.maxGenerations ?? 3);
    });

    const availableCount = Number(viewState.generationsRemaining ?? viewState.maxGenerations ?? 3);
    const availableLabel = `${pluralizeGenerations(availableCount)}.`;

    availableLabelNodes.forEach((node) => {
      node.textContent = availableLabel;
    });

    maxGenerationsNodes.forEach((node) => {
      node.textContent = String(viewState.maxGenerations ?? 3);
    });

    if (sourcePreview && viewState.sourceImage?.url) {
      sourcePreview.src = viewState.sourceImage.url;
      sourcePreview.alt = viewState.sourceImage.fileName || '';
    }

    const previewImage = viewState.selectedImage
      || (viewState.candidates || []).slice(-1)[0]
      || null;

    if (resultPreview && previewImage?.url) {
      resultPreview.src = previewImage.url;
      resultPreview.alt = previewImage.fileName || '';
      resultPreview.dataset.imageId = String(previewImage.id || '');
    }

    const savedImage = viewState.selectedImage
      || (viewState.selectedImageId
        ? (viewState.candidates || []).find((item) => item.id === viewState.selectedImageId)
        : null)
      || null;

    if (savedPreview && savedImage?.url) {
      savedPreview.src = savedImage.url;
      savedPreview.alt = savedImage.fileName || '';
    }

    if (processingTitle) {
      processingTitle.textContent = `Генерация ${Math.max(1, viewState.currentGeneration || 1)} из ${viewState.maxGenerations || 3}`;
    }

    const currentSceneTitle = viewState.currentSceneTitle || '';

    currentSceneTitleNodes.forEach((node) => {
      node.textContent = currentSceneTitle;
    });

    if (currentSceneBadge) {
      currentSceneBadge.hidden = viewState.screen !== 'result' || !currentSceneTitle;
    }

    if (viewState.screen === 'processing') {
      if (!processingMessageTimer) {
        startProcessingMessageRotation();
      }
    } else {
      stopProcessingMessageRotation();
    }

    renderSourceBars();
    renderCandidates();
    renderScenes();
    setScreen(viewState.screen || 'upload');

    if (fileIdInput) {
      fileIdInput.value = viewState.selectedImageId ? String(viewState.selectedImageId) : '';
    }

    updateGenerateButtonsState();
    updateInteractionState();

    fieldRoot.querySelectorAll('.js-ai-image-keep').forEach((button) => {
      button.disabled = viewState.interactionDisabled
        || (Boolean(viewState.isProcessing) && viewState.screen === 'processing')
        || actionPending;
    });
  };

  const showProcessingScreen = () => {
    applyViewState({
      ...viewState,
      screen: 'processing',
      isProcessing: true,
      currentGeneration: Math.min((viewState.generationsUsed ?? 0) + 1, viewState.maxGenerations ?? 3),
      pollJobUuid: viewState.pollJobUuid || '',
    });
  };

  const resolveDisplayedImageId = () => {
    const previewImageId = Number(resultPreview?.dataset.imageId || 0);

    if (previewImageId > 0) {
      return previewImageId;
    }

    return viewState.selectedImage?.id
      || viewState.selectedImageId
      || selectedCandidateId
      || (viewState.candidates || []).slice(-1)[0]?.id
      || 0;
  };

  const pollJobStatus = (jobUuid) => {
    if (!statusUrlTemplate || !jobUuid) {
      return;
    }

    const statusUrl = appendEmbedQueryParams(
      statusUrlTemplate.replace('__JOB_UUID__', encodeURIComponent(jobUuid)),
      fieldRoot,
    );
    let pollAttempts = 0;
    const maxPollAttempts = 90;

    const tick = () => {
      pollAttempts += 1;

      fetch(statusUrl, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      })
        .then((response) => response.json())
        .then((payload) => {
          if (payload.status !== 'ok' || !payload.job) {
            throw new Error('Не удалось получить статус обработки');
          }

          const jobStatus = payload.job.status;
          const nextView = normalizeViewFromJob(payload.job, viewState, jobUuid);

          if (jobStatus === 'done' || jobStatus === 'error') {
            stopPolling();
            setActionPending(false);
            setButtonsBusy(false);
          }

          applyViewState(nextView);

          if (jobStatus === 'done') {
            if (fileIdInput && viewState.selectedImageId) {
              fileIdInput.value = String(viewState.selectedImageId);
            }

            return;
          }

          if (jobStatus === 'error') {
            showError(payload.job.errorMessage || 'Ошибка обработки изображения');
            return;
          }

          if (pollAttempts >= maxPollAttempts) {
            stopPolling();
            setActionPending(false);
            setButtonsBusy(false);
            applyViewState({
              ...nextView,
              screen: resolveScreenFromView(nextView),
              isProcessing: false,
              pollJobUuid: '',
            });
            showError('Обработка занимает слишком много времени. Обновите страницу или попробуйте позже.');
            return;
          }

          pollTimer = window.setTimeout(tick, 2000);
        })
        .catch((error) => {
          if (pollAttempts >= maxPollAttempts) {
            stopPolling();
            setActionPending(false);
            setButtonsBusy(false);
            showError(error.message || 'Не удалось получить статус обработки');
            return;
          }

          pollTimer = window.setTimeout(tick, 2000);
        });
    };

    tick();
  };

  const uploadFile = (file, options = {}) => {
    if (viewState.interactionDisabled) {
      showError(viewState.interactionDisabledReason || 'Заявка удалена. Генерация изображений недоступна.');
      return;
    }

    clearError();
    setActionPending(true);
    sourceUploadConfirmed = false;

    const formData = new FormData();
    formData.append('image', file);
    formData.append('workFieldUuid', workFieldUuid);
    formData.append('customFieldUuid', customFieldUuid);
    appendEmbedCredentials(formData, fieldRoot);

    if (options.replace) {
      formData.append('replace', '1');
    }

    postAiImageForm(uploadUrl, formData)
      .then((payload) => {
        if (payload.workFieldUuid) {
          syncWorkFieldUuidInDom(payload.workFieldUuid);
        }

        if (!payload.view?.sourceImage?.url) {
          sourceUploadConfirmed = false;
          showError('Фото не сохранилось как исходник. Загрузите файл ещё раз.');
          return;
        }

        pinSourceImage(payload.view.sourceImage);
        sourceUploadConfirmed = true;

        const nextView = {
          ...payload.view,
          sourceImage: payload.view.sourceImage,
          screen: 'source-ready',
          isProcessing: false,
          pollJobUuid: '',
          canGenerate: payload.view.canGenerate !== false,
        };

        applyViewState(nextView);

        if (fileIdInput) {
          fileIdInput.value = payload.view?.selectedImageId ? String(payload.view.selectedImageId) : '';
        }
      })
      .catch((error) => {
        showError(error.message || 'Ошибка загрузки');
      })
      .finally(() => {
        setActionPending(false);
        isReplaceUpload = false;
        updateGenerateButtonsState();
        updateInteractionState();
      });
  };

  const startGeneration = () => {
    if (actionPending || viewState.interactionDisabled) {
      return;
    }

    syncWorkFieldUuidInDom(fieldRoot.dataset.workFieldUuid || workFieldUuid);

    const sceneId = resolveGenerationSceneId();

    if (!sceneId) {
      showError('Выберите сцену для генерации');
      return;
    }

    if (viewState.canGenerate === false) {
      showError('Генерация сейчас недоступна. Обновите страницу или дождитесь завершения текущей обработки.');
      return;
    }

    const pinnedSource = viewState.sourceImage?.url
      ? viewState.sourceImage
      : restorePinnedSourceImage();

    if (!sourceUploadConfirmed || !pinnedSource?.url) {
      showError('Дождитесь завершения загрузки исходного фото и попробуйте снова.');
      return;
    }

    pinSourceImage(pinnedSource);
    viewState = { ...viewState, sourceImage: pinnedSource };

    clearError();
    stopPolling();
    setActionPending(true);
    setButtonsBusy(true, 'Генерация...');
    showProcessingScreen();

    const formData = new FormData();
    formData.append('workFieldUuid', workFieldUuid);
    formData.append('sceneId', sceneId);
    appendEmbedCredentials(formData, fieldRoot);

    postAiImageForm(generateUrl, formData)
      .then((payload) => {
        if (!payload.jobUuid) {
          throw new Error('Не удалось запустить генерацию');
        }

        if (sceneId && isSceneIdValid(sceneId)) {
          pickScene(sceneId);
        }

        applyViewState({
          ...(payload.view || viewState),
          sourceImage: payload.view?.sourceImage?.url ? payload.view.sourceImage : pinnedSource,
          selectedSceneId: sceneId,
          screen: 'processing',
          isProcessing: true,
          pollJobUuid: payload.jobUuid,
          currentGeneration: Math.min((viewState.generationsUsed ?? 0) + 1, viewState.maxGenerations ?? 3),
        });

        pollJobStatus(payload.jobUuid);
      })
      .catch((error) => {
        setActionPending(false);
        setButtonsBusy(false);
        applyViewState({
          ...viewState,
          sourceImage: pinnedSource,
          screen: 'source-ready',
          isProcessing: false,
          pollJobUuid: '',
          canGenerate: true,
        });
        showError(error.message || 'Ошибка запуска генерации');
      });
  };

  const selectCandidate = (imageId) => {
    clearError();
    setActionPending(true);
    setButtonsBusy(true);

    const formData = new FormData();
    formData.append('workFieldUuid', workFieldUuid);
    formData.append('imageId', String(imageId));
    appendEmbedCredentials(formData, fieldRoot);

    return postAiImageForm(selectUrl, formData)
      .then((payload) => {
        applyViewState(payload.view);

        if (fileIdInput && payload.view?.selectedImageId) {
          fileIdInput.value = payload.view.selectedImageId;
        }
      })
      .finally(() => {
        setActionPending(false);
        setButtonsBusy(false);
        applyViewState(viewState);
      });
  };

  const reopenPick = () => {
    clearError();
    setActionPending(true);
    setButtonsBusy(true);

    const formData = new FormData();
    formData.append('workFieldUuid', workFieldUuid);
    appendEmbedCredentials(formData, fieldRoot);

    return postAiImageForm(reopenPickUrl, formData)
      .then((payload) => {
        applyViewState(payload.view);
      })
      .catch((error) => {
        showError(error.message || 'Ошибка открытия выбора вариантов');
      })
      .finally(() => {
        setActionPending(false);
        setButtonsBusy(false);
        applyViewState(viewState);
      });
  };

  const handleActionClick = (event, actionButton) => {
    if (!fieldRoot.contains(actionButton)) {
      return;
    }

    if (actionButton.disabled || actionPending || viewState.interactionDisabled) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (actionButton.classList.contains('js-ai-image-reopen-pick')) {
      reopenPick();
      return;
    }

    if (actionButton.classList.contains('js-ai-image-generate')
      || actionButton.classList.contains('js-ai-image-generate-more')) {
      startGeneration();
      return;
    }

    if (actionButton.classList.contains('js-ai-image-keep')) {
      const imageId = resolveDisplayedImageId();

      if (!imageId) {
        showError('Нет изображения для выбора');
        return;
      }

      selectCandidate(imageId).catch((error) => {
        showError(error.message || 'Ошибка сохранения');
      });
      return;
    }

  };

  fieldRoot.addEventListener('mousedown', (event) => {
    const trigger = event.target.closest('.js-ai-image-file-trigger');

    if (!trigger || !fieldRoot.contains(trigger)) {
      return;
    }

    if (actionPending || viewState.interactionDisabled) {
      event.preventDefault();
      return;
    }

    isReplaceUpload = trigger.dataset.replace === '1';
  });

  fileInput?.addEventListener('change', (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    uploadFile(file, { replace: isReplaceUpload });
    event.target.value = '';
  });

  if (dropzone) {
    ['dragenter', 'dragover'].forEach((eventName) => {
      dropzone.addEventListener(eventName, (event) => {
        event.preventDefault();
        dropzone.classList.add('is-dragover');
      });
    });

    ['dragleave', 'drop'].forEach((eventName) => {
      dropzone.addEventListener(eventName, (event) => {
        event.preventDefault();
        dropzone.classList.remove('is-dragover');
      });
    });

    dropzone.addEventListener('drop', (event) => {
      const file = event.dataTransfer?.files?.[0];

      if (file) {
        uploadFile(file, { replace: isReplaceUpload });
      }
    });
  }

  applyViewState(viewState);

  if (viewState.isProcessing && viewState.pollJobUuid && !viewState.interactionDisabled) {
    pollJobStatus(viewState.pollJobUuid);
  }

  const confirmSelectionIfNeeded = async () => {
    if (viewState.selectionConfirmed || viewState.interactionDisabled) {
      return;
    }

    const imageId = resolveDisplayedImageId();

    if (!imageId) {
      return;
    }

    const isCandidate = (viewState.candidates || []).some((candidate) => candidate.id === imageId);

    if (!isCandidate) {
      return;
    }

    await selectCandidate(imageId);
  };

  return {
    handleActionClick,
    confirmSelectionIfNeeded,
  };
}

function getAiImageFieldController(fieldRoot) {
  let controller = fieldControllers.get(fieldRoot);

  if (!controller) {
    controller = createAiImageFieldController(fieldRoot);
    fieldControllers.set(fieldRoot, controller);
    fieldRoot.dataset.aiImageInitialized = '1';
  }

  return controller;
}

async function confirmAiImageSelectionsBeforeSave() {
  bindDocumentClick();

  const tasks = [];

  document.querySelectorAll('.js-ai-image-field').forEach((fieldRoot) => {
    try {
      tasks.push(getAiImageFieldController(fieldRoot).confirmSelectionIfNeeded());
    } catch (error) {
      console.error('[ai-image-field] confirm before save failed', error);
    }
  });

  await Promise.all(tasks);
}

bindDocumentClick();

if (document.readyState !== 'loading') {
  initAiImageFields();
}

export { initAiImageFields, confirmAiImageSelectionsBeforeSave };
